/**
 * Uncaught errors go to the same place as a dead tab (D96).
 *
 * The black box covers a tab the browser takes away. The other thing a player calls "a crash" is an
 * exception nobody sees: the page stops reacting and there is no console on a phone to say why.
 * Every uncaught error and every promise nobody caught is therefore reported once a minute at most,
 * with the message, the top of the stack and the screen the player was on.
 */
export function installErrorReporting(
  send: (step: string, note: Record<string, unknown>) => void,
  minIntervalMs = 60_000,
): () => void {
  let lastSent = -Infinity;
  const report = (step: string, reason: unknown, fallback: string) => {
    const now = Date.now();
    if (now - lastSent < minIntervalMs) return;
    lastSent = now;
    const error = reason instanceof Error ? reason : null;
    const message = (error?.message ?? (typeof reason === "string" ? reason : fallback) ?? "").slice(0, 300);
    const note: Record<string, unknown> = {
      message,
      stack: (error?.stack ?? "").slice(0, 600),
      route: typeof location !== "undefined" ? location.pathname : "",
      agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 120) : "",
    };
    try {
      send(step, note);
    } catch {
      /* a report that cannot be sent is not worth a second error */
    }
  };
  const onError = (event: ErrorEvent) => report("js error", event.error, event.message);
  const onRejection = (event: Event) => report("js rejection", (event as PromiseRejectionEvent).reason, "unhandled rejection");
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  return () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
  };
}
